import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContactDialog, ENTRY_CSS, EntryFooter, EntryHeader } from "../components/AugustEntry";

// The November board meeting (Group 19 admissions) is its own app, served at tamutrip.com/admissions:
// Firebase Hosting rewrites the path to the admissions-relay Cloud Run service (admissions-proxy/).
export const NOVEMBER_URL = "/admissions";

const GOLD = "#d3a978";
const MAROON = "#b43c3c";

/**
 * tamutrip.com front door: pick the August board meeting (this app, at /august) or the
 * November board meeting (the admissions platform). Both projects keep their own landing pages.
 */
export default function Home() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [showContact, setShowContact] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);
  const anim = (delay) => ({ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(14px)", transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s` });

  const meetings = [
    {
      key: "august",
      month: "August",
      title: "August Board Meeting",
      desc: "Student profiles and resumes, the resume book, board week RSVPs and candidate preferences.",
      accent: MAROON,
      glow: "rgba(160,0,0,0.35)",
      go: () => navigate("/august"),
    },
    {
      key: "november",
      month: "November",
      title: "November Board Meeting",
      desc: "Group 19 admissions: application reviews, Board Day interviews and the Decision Room.",
      accent: GOLD,
      glow: "rgba(211,169,120,0.3)",
      go: () => { window.location.href = NOVEMBER_URL; },
    },
  ];

  return (
    <div className="ae" style={{ background: "#07070a", position: "relative", overflow: "hidden" }}>
      <style>{ENTRY_CSS + `
        @keyframes driftA { 0%,100% { transform: translate(-15%, -10%) scale(1); } 50% { transform: translate(10%, 15%) scale(1.15); } }
        @keyframes driftB { 0%,100% { transform: translate(15%, 10%) scale(1.1); } 50% { transform: translate(-10%, -12%) scale(0.95); } }
        .meeting-card { transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease; }
        .meeting-card:hover, .meeting-card:focus-visible { transform: translateY(-6px); outline: none; }
        .meeting-card:hover .meeting-arrow, .meeting-card:focus-visible .meeting-arrow { transform: translateX(4px); }
        .meeting-arrow { transition: transform 0.25s ease; display: inline-block; }
        @keyframes sigPulse { 0%,100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 1; transform: scale(1.2); } }
        .signature-dot { animation: sigPulse 2.5s ease infinite; }
        .signature-badge:hover { border-color: rgba(211,169,120,0.7) !important; box-shadow: 0 0 20px rgba(160,0,0,0.4) !important; }
        @media (prefers-reduced-motion: reduce) { .home-glow, .signature-dot { animation: none !important; } .meeting-card:hover { transform: none; } }
      `}</style>

      {/* August maroon on the left, November champagne on the right: the page sits between the two. */}
      <div className="home-glow" style={{ position: "fixed", top: "10%", left: "-10%", width: 700, height: 700, background: "radial-gradient(circle, rgba(160,0,0,0.35) 0%, transparent 65%)", filter: "blur(90px)", animation: "driftA 40s ease-in-out infinite", pointerEvents: "none" }} />
      <div className="home-glow" style={{ position: "fixed", bottom: "-10%", right: "-10%", width: 700, height: 700, background: "radial-gradient(circle, rgba(211,169,120,0.28) 0%, transparent 65%)", filter: "blur(90px)", animation: "driftB 46s ease-in-out infinite", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 2, ...anim(0) }}>
        <EntryHeader product={null} home="/" label="TRIP">
          <button className="ae-navlink" onClick={() => setShowContact(true)}>Contact</button>
        </EntryHeader>
      </div>

      <main style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "40px clamp(16px, 5vw, 48px) 60px" }}>
        <div style={{ textAlign: "center", ...anim(0.1) }}>
          <div style={{ color: GOLD, fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 500 }}>Trading, Risk &amp; Investments Program</div>
          <h1 style={{ margin: "18px 0 0", fontSize: "clamp(38px, 6.5vw, 72px)", fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1.02 }}>Choose your board meeting</h1>
          <p style={{ margin: "16px auto 0", maxWidth: 520, color: "rgba(255,255,255,0.55)", fontSize: 16, lineHeight: 1.55 }}>Students, board members and TRIP staff: pick the meeting you’re here for.</p>
        </div>

        <div style={{ marginTop: 52, width: "100%", maxWidth: 920, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 20 }}>
          {meetings.map((m, i) => (
            <button
              key={m.key}
              type="button"
              className="meeting-card"
              onClick={m.go}
              aria-label={`Go to the ${m.title}`}
              style={{ ...anim(0.2 + i * 0.1), textAlign: "left", cursor: "pointer", color: "white", font: "inherit", background: "rgba(255,255,255,0.035)", border: `1px solid ${m.accent}55`, borderRadius: 20, padding: "30px 28px 26px", boxShadow: `0 0 0 rgba(0,0,0,0), 0 30px 60px -40px ${m.glow}`, backdropFilter: "blur(8px)" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = m.accent; e.currentTarget.style.boxShadow = `0 0 40px -6px ${m.glow}, 0 30px 60px -30px ${m.glow}`; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${m.accent}55`; e.currentTarget.style.boxShadow = `0 30px 60px -40px ${m.glow}`; }}
            >
              <div style={{ color: m.accent, fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>{m.month}</div>
              <div style={{ marginTop: 12, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em" }}>{m.title}</div>
              <p style={{ margin: "12px 0 0", color: "rgba(255,255,255,0.6)", fontSize: 14.5, lineHeight: 1.55 }}>{m.desc}</p>
              <div style={{ marginTop: 26, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12.5 }}>Students · Board · Admins</span>
                <span style={{ color: m.accent, fontWeight: 600, fontSize: 15 }}>Enter <span className="meeting-arrow">→</span></span>
              </div>
            </button>
          ))}
        </div>
      </main>

      <div style={{ position: "relative", zIndex: 2, ...anim(0.4) }}>
        <EntryFooter note={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12.5, letterSpacing: 0.3 }}>Designed &amp; built by</span>
            <span className="signature-badge" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "linear-gradient(135deg, rgba(160,0,0,0.3), rgba(211,169,120,0.14))", border: "1px solid rgba(211,169,120,0.35)", borderRadius: 20, padding: "5px 14px 5px 10px", fontSize: 13, color: "#f3dcc0", fontWeight: 700, letterSpacing: 0.4, boxShadow: "0 0 14px rgba(160,0,0,0.25)", transition: "all 0.2s" }}>
          <span className="signature-dot" style={{ width: 6, height: 6, background: GOLD, borderRadius: "50%", display: "inline-block", flexShrink: 0, boxShadow: `0 0 6px ${GOLD}` }} />
          KK Hari
            </span>
          </span>
        } />
      </div>
      <ContactDialog open={showContact} onClose={() => setShowContact(false)} />
    </div>
  );
}
